import React from 'react'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { Box, Typography } from '@mui/material'
import { CogIcon } from '@heroicons/react/24/outline'
import { DropdownMenu, DropdownMenuItem } from '../../interaxDesignSystemComponents'
import Avatar from './Avatar'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'
import { TESTIDS } from '../../config/testids'

export interface ProfileBarProps {
  name: string
  email: string
  role?: string
  className?: string
  onAccountSettings?: () => void
  onLogout?: () => void
}

export const ProfileBar: React.FC<ProfileBarProps> = ({
  name,
  email,
  role: _role,
  className = '',
  onAccountSettings,
  onLogout
}) => {
  const { t } = useTranslation()

  // Define dropdown menu items
  const dropdownItems: DropdownMenuItem[] = [
    {
      id: 'account-settings',
      label: t('auth.accountSettings'),
      icon: <CogIcon style={{ width: '16px', height: '16px', color: 'inherit' }} />,
      onClick: () => onAccountSettings?.(),
      variant: 'default'
    },
    {
      id: 'logout',
      label: t('auth.logout'),
      onClick: () => onLogout?.(),
      variant: 'danger'
    }
  ]

  return (
    <DropdownMenu
      className={className}
      trigger={
        <Box
          data-testid={TESTIDS.SIDEBAR_PROFILE_TRIGGER}
          sx={{
            p: 2,
            borderBottom: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: INTERAX_COLORS.white10
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar name={name} size="md" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: INTERAX_TYPOGRAPHY.headline4.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.headline4.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.headline4.fontSize,
                  lineHeight: '20px',
                  color: INTERAX_COLORS.white,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: INTERAX_TYPOGRAPHY.fieldText2.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.fieldText2.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.fieldText2.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.fieldText2.lineHeight,
                  color: INTERAX_COLORS.thunderGrey,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {email}
              </Typography>
            </Box>
          </Box>
        </Box>
      }
      items={dropdownItems}
      placement="bottom-left"
      width="100%"
    />
  )
}

export default ProfileBar
